import React, { useEffect, useState } from 'react';
import { IconButton, Icon, TextField, Tooltip } from '@mui/material';
import useStyles from './TaskDetail.styles';
import { useDispatch, useSelector } from 'react-redux';
import { get, size, first, set, flatten, upperFirst } from 'global/lib/lodash';
import { formatDateFromNow } from 'global/utils/formatDate';
import { getTaskStatus, stopProp } from 'global/utils';
import Image from 'global/components/image';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Comment from 'global/components/comment';
import sendData from 'redux/actions/sendData';
import DropArea from 'global/components/drop-area';
import Confirm from 'global/components/confirm';
import { setView } from 'redux/actions/view';
import { useUser } from 'global/hooks/useUser';
import { Text } from 'global/components/text';
import { Row } from 'global/components/row';
import { UploadFiles } from 'redux/actions/imageUploader';
import { Button } from '../../../global/components/Button';
import { taskStringToNum } from '../../../global/utils';
import { useScreen } from '../../../global/styles';
import { userHasPermissions } from 'redux/actions/userLogin';

export const allowedToEdit = (task, comments = [], opts = {}) => {
  opts = {
    user: null,
    ...opts,
  }

  const authors = get(task, 'authors', []);
  const assignees = get(task, 'assignees', []);
  const isAuthor = authors.find((a) => a.id == get(opts, 'user.me.id'));
  const isAssignee = assignees.find((a) => a.id == get(opts, 'user.me.id'));

  const obj = {
    show: false,
    disabled: false,
    delete: false,
    status: 10,
  };
  if (!task) {
    return obj;
  } else {
    if (isAssignee) {
      obj.show = true;
    }
  }

  if (isAuthor || isAssignee) {
    obj.status = task.status;
  }

  if (isAuthor) {
    obj.delete = true;
  }

  if (size(comments)) {
    obj.status = 10;
  }

  if (userHasPermissions(['manageAccount']) && (!isAuthor || !isAssignee)) {
    obj.status = get(opts, 'user.userRealm.id') == get(task, 'clientId') && get(opts, 'user.userRealm.id') == get(task, 'vendorId') ? task.status : 10;
    obj.delete = true;
  }

  obj.show = true;
  return obj;
};

export const CommentContainer = (props) => {
  const { comments, formState, data, onSubmitComment } = props;

  const { isMobile } = useScreen();
  const classes = useStyles({ ...props, isMobile });

  let [createComment, setCreateComment] = useState();
  const [confirmProps, setConfirmProps] = useState({ maxWidth: 'sm' });
  const photoDrop = true;

  const dispatch = useDispatch();
  const uhook = useUser();
  const { me } = uhook;
  const dontAskTaskDelete = useSelector((state) => get(state, 'view.dontAskTaskDelete'));
  const status = getTaskStatus(data);
  const [state, setState] = useState({
    display: 'comment',
    loading: false,
    comments: []
  })

  const ate = allowedToEdit(data, comments, { user: uhook })

  const submitComment = async () => {
    const comms = [...comments, createComment];
    if (!onSubmitComment) {
      try {
        setState(s => ({ ...s, loading: true }))
        const _comms = comms.filter((comment) => comment.isNew);
        const task = get(props, 'task', {});
        const currentTaskStatus = getTaskStatus(task);
        const tid = get(task, 'id');
  
        const result = await Promise.all(
          _comms.map(async (comment) => {
            const stat = taskStringToNum(comment.newStatus);
  
            const _data = {
              data: {
                type: 'comments',
                attributes: {
                  user_id: get(me, 'id'),
                  ...comment,
                  commentable_id: tid,
                  commentable_type: 'task',
                  body: get(comment, 'body', ''),
                  meta: {
                    showCorrection: stat !== currentTaskStatus.status,
                    status: stat,
                  },
                },
              },
            };
  
            await UploadFiles(_data, { attributeType: 'images' });

            await sendData({
              url: `/tasks/${tid}`,
              method: 'patch',
              data: {
                data: {
                  id: tid,
                  type: 'tasks',
                  attributes: {
                    status: stat,
                  }
                }
              }
            })
  
            return await sendData({
              url: `/comments`,
              method: 'post',
              data: _data,
            });
          })
        );
        return props.onClose && props.onClose(result)
      } catch (err) {} finally {
        setState(s => ({ ...s, loading: false }))
      }
    }
    await onSubmitComment(comms);
    setCreateComment();
  };

  const clickComment = (opts = {}) => {
    createComment = {
      isNew: true,
      showToggle: false,
      isCorrection: true,
      newStatus: 'closed',
      body: '',
      ...opts,
    };
    setCreateComment({ ...createComment });
  };

  const onFilesChange = (files) => {
    set(createComment, 'images', [...files]);
    setCreateComment({ ...createComment });
  };

  const deleteImage = (file, index) => {
    const photos = get(createComment, 'images', []);
    photos.splice(index, 1);
    set(createComment, 'images', photos);
    setCreateComment({ ...createComment });
  };

  const closeoutClick = () => {
    const closeConfirm = () => {
      setConfirmProps((cp) => ({ ...cp, title: '' }));
    };

    const deleteImages = async () => {
      try {
        await Promise.all(
          allImages().map(async (ci) => {
            await sendData({
              url: `/media/${ci.id}`,
              method: 'delete',
            });
          })
        );
        set(formState, 'images', []);
      } catch (err) {
        alert(`Error: ${err.message}`);
      }
    };

    const closetask = () => {
      clickComment({ newStatus: 'closed', body: 'Task closed' });
      submitComment();
    };

    const completeCloseout = async () => {
      await deleteImages();
      closetask();
      closeConfirm();
    };

    if (status.status !== 1) {
      //author direct closeout... special conditions
      if (dontAskTaskDelete) {
        completeCloseout();
      } else {
        setConfirmProps({
          ...confirmProps,
          title: 'Are you sure?',
          content: 'You are closing a task without uploading correction photos first, this will also delete all the task photos. Would you like to continue?',
          okayText: 'Closeout and Delete Photos',
          showDontAsk: true,
          onCancel: closeConfirm,
          onOk: async (dontAsk) => {
            dispatch(setView({ dontAskTaskDelete: dontAsk }));
            await completeCloseout();
          },
        });
      }
    } else {
      closetask();
    }
  };

  const commentDates = () => {
    return `last updated ${formatDateFromNow(get(first(comments), 'createdAt', new Date()))}`;
  };

  const commentDoneDisabled = () => {
    const isCorrection = get(createComment, 'isCorrection');
    const bodysize = size(get(createComment, 'body'));
    let dis = !bodysize;
    let text = 'Description required to submit.';
    if (isCorrection) {
      dis = !size(get(createComment, 'images')) || !bodysize;
      text = 'Description and photo required to add correction.';
    }
    return {
      disabled: dis,
      text,
    };
  };

  const allImages = () => {
    const commentHas = flatten(comments.filter((c) => !!size(c.images)).map((c) => c.images));
    const taskHas = get(data, '0.images', []);

    return [...(commentHas || []), ...(taskHas || [])];
  };

  useEffect(() => {
    if (props.openInCreate) {
      clickComment(props.openInCreate)
    }
  }, []);

  useEffect(() => {
    const disp = !state.display ? 'comment' : state.display;
    const c = disp === 'all' ? comments : comments.filter(c => {
      const isHist = !!get(c, 'meta.valuesChanged');
      if (disp == 'comment') {
        return !isHist;
      } else if (disp == 'history') {
        return isHist;
      }
    })
    setState(s => ({ ...s, comments: c, display: disp }))
  }, [comments, state.display]);

  const hasImages = !!size(allImages());

  const closeoutTitle = `Only the Author of a task can use the "Closeout" button to submit a comment that changes the status to "Closed".${
    hasImages ? ' Closing a task without uploading a correction will also remove the original task photos.' : ''
  }`;

  const showComments = get(props, 'showComments', true);

  return (
    <div className={classes.commentContainer}>
      {showComments && <div className={classes.commentsWrap}>
        <div className={classes.commentWrapTitle}>
          <Text type="title_large">Activity</Text>
          <div className={classes.commentCount}>{commentDates()}</div>
        </div>
        <div className={classes.showWrap}>
          <Row>
            <Text>Show: </Text>
            <ToggleButtonGroup color="secondary" size="small" value={state.display} onChange={(e, v) => setState(s => ({ ...s, display: v }))} exclusive={true}>
              <ToggleButton value="all">
                All
              </ToggleButton>
              <ToggleButton value="comment">
                Comments
              </ToggleButton>
              <ToggleButton value="history">
                History
              </ToggleButton>
            </ToggleButtonGroup>
          </Row>
        </div>
        {state.comments.map((c, i) => (
          <Comment key={i} {...c} />
        ))}
      </div>}

      {createComment && (
        <div className={classes.createCommentContainer}>
          <div className={classes.leftIcon}>
            {getTaskStatus(createComment.newStatus).icon}
          </div>

          <div className={classes.textContainer}>
            <div>
              <div className={classes.titleContainer}>{createComment && createComment.isCorrection ? 'Add Correction' : 'Add Comment'}</div>

              {createComment.showToggle ? (
                <div className={classes.correctionToggleContainer}>
                  <ToggleButtonGroup fullWidth={true} size={'small'} onChange={(e, newStatus) => setCreateComment({ ...createComment, newStatus })} exclusive={true} value={createComment.newStatus}>
                    <ToggleButton value="pending">
                      <Icon className={classes.correctionBtnIcon}>check_circle_outline</Icon> Pending
                    </ToggleButton>
                    <ToggleButton value="closed" disabled={ate.disabled}>
                      <Icon className={classes.correctionBtnIcon}>check_circle_fill</Icon> Closed
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
              ) : createComment && createComment.isCorrection ? (
                <Row>
                  <Text>Change Status To</Text>
                  <div style={{ marginLeft: 5 }}>{getTaskStatus(createComment.newStatus).icon}</div>
                  <Text>{upperFirst(createComment.newStatus)}</Text>
                </Row>
              ) : null}

              <TextField
                multiline={true}
                value={createComment.body}
                rows={5}
                variant="outlined"
                fullWidth={true}
                className={classes.commentText}
                onChange={(e) => setCreateComment({ ...createComment, body: e.target.value })}
              />
            </div>

            <div>
              {photoDrop && (
                <DropArea
                  accept={'image/*'}
                  onFilesChange={onFilesChange}
                  files={get(createComment, 'images', [])}
                  renderContent={() => <div>Drop Files here. Or click here.</div>}
                  renderFile={(file, i) => {
                    const loc = `images.${i}.caption`;
                    return (
                      <div onClick={stopProp}>
                        <Image style={{ overflow: 'hidden' }} useNaturalDims={false} imageStyle={{ width: 'auto', margin: 'auto', right: 0, bottom: 0 }} src={file.data || file.src} />
                        <TextField
                          placeholder="caption"
                          value={get(createComment, loc)}
                          onChange={(e) => {
                            set(createComment, loc, e.target.value);
                            setCreateComment({ ...createComment });
                          }}
                          className={classes.comment}
                        />
                        <IconButton
                          className={classes.imageDelete}
                          onClick={() => deleteImage(file, i)}
                          size="large">
                          <Icon>close</Icon>
                        </IconButton>
                      </div>
                    );
                  }}
                />
              )}

              <div className={classes.submitButtons}>
                <Button variant={'contained'} color="primary" startIcon={<Icon>close</Icon>} onClick={() => { if (!showComments) { return props.onClose() } setCreateComment() }}>
                  Cancel
                </Button>

                <Tooltip title={commentDoneDisabled().text}>
                  <span>
                    <Button variant={'contained'} loading={state.loading} color="secondary" startIcon={<Icon>check</Icon>} disabled={commentDoneDisabled().disabled} onClick={() => submitComment()}>
                      Done
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      )}

      {!createComment && (
        <div className={classes.commentBtnContainer}>
          <Button className={classes.commentBtn} variant={'contained'} color="secondary" startIcon={<Icon>add_comment</Icon>} onClick={() => clickComment({ newStatus: status.title, isCorrection: false })}>
            Comment
          </Button>

          {status.status === 0 && (
            <Tooltip title={`Only the author or assignee of a task can change its status.`}>
              <span>
                <Button
                  className={classes.commentBtn}
                  variant={'contained'}
                  color="secondary"
                  disabled={ate.disabled}
                  startIcon={getTaskStatus('pending').icon}
                  onClick={() => clickComment({ newStatus: 'pending' })}
                >
                  Pending
                </Button>
              </span>
            </Tooltip>
          )}

          {(status.status === 1 || (status.status !== 2)) && (
            <Tooltip title={closeoutTitle}>
              <span>
                <Button className={classes.commentBtn} variant={'contained'} color="secondary" startIcon={getTaskStatus('closed').icon} disabled={ate.disabled} onClick={() => clickComment({ newStatus: 'closed' })}>
                  Close
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      )}

      <Confirm {...confirmProps} />
    </div>
  );
};
